import { Static, Type } from '@sinclair/typebox'

/**
 * Interactive cards the AI Builder can put in the chat. The orchestrator is
 * stateless: a reply is re-associated by the card's own typed fields
 * (`cardTargetOf`), never by a store lookup or by splitting an id string.
 */

/** The one list of card types every target and reply reads */
export const AiBuilderCardType = Type.Union([
    Type.Literal('connection_gate'),
    Type.Literal('dropdown_choice'),
    Type.Literal('write_preview'),
    Type.Literal('field_choice'),
    Type.Literal('loop_lists_choice'),
])
export type AiBuilderCardType = Static<typeof AiBuilderCardType>

export const CardDropdownOption = Type.Object({
    label: Type.String(),
    value: Type.Unknown(),
})
export type CardDropdownOption = Static<typeof CardDropdownOption>

export const ConnectionChoiceCard = Type.Object({
    type: Type.Literal('connection_gate'),
    stepName: Type.String(),
    pieceName: Type.String(),
    pieceDisplayName: Type.String(),
    /** Existing candidate connections; empty means "create one" is the only path */
    connections: Type.Array(
        Type.Object({
            externalId: Type.String(),
            displayName: Type.String(),
        }),
    ),
})
export type ConnectionChoiceCard = Static<typeof ConnectionChoiceCard>

/** Options a choice card shows before any search — the server's shortlist size */
export const AI_BUILDER_CARD_VISIBLE_OPTIONS = 5

export const DropdownChoiceCard = Type.Object({
    type: Type.Literal('dropdown_choice'),
    stepName: Type.String(),
    propName: Type.String(),
    propDisplayName: Type.String(),
    /** Shown up front: the options matching the target the user named, else the first ones */
    shortlist: Type.Array(CardDropdownOption),
    /** Full list for the card's search / "show more" */
    allOptions: Type.Array(CardDropdownOption),
})
export type DropdownChoiceCard = Static<typeof DropdownChoiceCard>

export const WritePreviewCard = Type.Object({
    type: Type.Literal('write_preview'),
    stepName: Type.String(),
    actionDisplayName: Type.String(),
    /** The exact input the step would run with */
    input: Type.Record(Type.String(), Type.Unknown()),
    outputIsRead: Type.Optional(Type.Boolean()),
})
export type WritePreviewCard = Static<typeof WritePreviewCard>

/**
 * Several data share the name the user asked for (two "montant" fields): the
 * user picks which one goes into the field. Option value: the field's whole
 * value with the reference swapped.
 */
export const FieldChoiceCard = Type.Object({
    type: Type.Literal('field_choice'),
    stepName: Type.String(),
    propName: Type.String(),
    /** A dynamic sub-field's key (a Sheets column); absent for a top-level prop */
    fieldKey: Type.Optional(Type.String()),
    fieldDisplayName: Type.String(),
    shortlist: Type.Array(CardDropdownOption),
    allOptions: Type.Array(CardDropdownOption),
})
export type FieldChoiceCard = Static<typeof FieldChoiceCard>

/**
 * Several lists carry the same columns (two tables of a form): the user picks
 * which ones the loop walks. Option value: the chosen list paths.
 */
export const LoopListsChoiceCard = Type.Object({
    type: Type.Literal('loop_lists_choice'),
    stepName: Type.String(),
    shortlist: Type.Array(CardDropdownOption),
    allOptions: Type.Array(CardDropdownOption),
    /** Value of the "all these lists" option, always offered */
    allLists: Type.Array(Type.String()),
})
export type LoopListsChoiceCard = Static<typeof LoopListsChoiceCard>

export const AiBuilderCard = Type.Union([
    ConnectionChoiceCard,
    DropdownChoiceCard,
    WritePreviewCard,
    FieldChoiceCard,
    LoopListsChoiceCard,
])
export type AiBuilderCard = Static<typeof AiBuilderCard>

/** Which card a reply targets: a prop for a dropdown or a field choice, a sub-field key for a field choice */
export const cardTargetOf = (
    card: AiBuilderCard,
): { type: AiBuilderCardType, stepName: string, propName?: string, fieldKey?: string } => ({
    type: card.type,
    stepName: card.stepName,
    propName: card.type === 'dropdown_choice' || card.type === 'field_choice' ? card.propName : undefined,
    fieldKey: card.type === 'field_choice' ? card.fieldKey : undefined,
})

/** The two answers a write-preview card accepts */
export const WRITE_PREVIEW_REPLY = {
    TEST: 'test',
    SKIP: 'skip',
} as const
